import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ActivationSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/TranscriptionEdit/ActivationSection';
import SpeakerSeparationSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/TranscriptionEdit/SpeakerSeparationSection';
import StepsSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/TranscriptionEdit/StepsSection';
import VocabularySection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/TranscriptionEdit/VocabularySection';
import BackLink from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/BackLink';
import ScreenHeading from '@UI/Pages/Administration/DocumentGroups/Shared/ScreenHeading';
import type OrganizationWorkflowView from '@UI/Store/OrganizationWorkflows/Views/OrganizationWorkflowView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from '@verclam/ui-kit';

const screenStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	max-width: 51.25rem;
	width: 100%;
`;

type Props = {
	readonly workflow: OrganizationWorkflowView;
	readonly organizationId: number;
};

/**
 * One automatic transcription: whether it runs, its fixed steps, and the vocabulary it is given. The
 * AI usage that carries the vocabulary comes from the transcription step itself, so the notes and the
 * interviews each reach their own settings.
 */
const TranscriptionEdit: React.FC<Props> = ({ workflow, organizationId }) => {
	const navigate = useNavigate();

	const usage = React.useMemo(
		() => workflow.steps.find((step) => step.type.value === 'transcription')?.usage?.value ?? null,
		[workflow.steps],
	);
	const kindLabel = usage === 'dictation' ? 'notes' : 'entretiens';

	const handleBackClick = React.useCallback(() => {
		navigate('/administration/document-groups');
	}, [navigate]);

	return (
		<div css={screenStyle}>
			<BackLink label="Groupes de documents" onClick={handleBackClick} />
			<ScreenHeading
				eyebrow="Transcription automatique"
				title={workflow.label}
				chip={
					<Tag
						value={workflow.isActive ? 'Actif' : 'Désactivé'}
						severity={workflow.isActive ? 'success' : 'secondary'}
						rounded
					/>
				}
			/>
			<ActivationSection
				organizationId={organizationId}
				workflowKey={workflow.key}
				workflowLabel={workflow.label}
				isActive={workflow.isActive}
				kindLabel={kindLabel}
			/>
			<StepsSection steps={workflow.steps} />
			<ConditionalLoader data={workflow.hasSpeakerSeparationStep}>
				<SpeakerSeparationSection
					organizationId={organizationId}
					workflowKey={workflow.key}
					separatesSpeakers={workflow.separatesSpeakers}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={usage}>
				{(presentUsage) => <VocabularySection organizationId={organizationId} usage={presentUsage} />}
			</ConditionalLoader>
		</div>
	);
};

export default TranscriptionEdit;
